import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { getFuseGridButtonIcon } from '../icons/FuseGridButtonIcons'
import './ExcelWorkbookChrome.css'

export type ExcelSheetTabKind = 'data-source' | 'worksheet' | 'dashboard' | 'spreadsheet' | 'cfo-home' | 'cfo-core' | 'cfo-presentation'

export type ExcelSheetTabEntry = {
  id: string
  name: string
  kind: ExcelSheetTabKind
  sourceKind?: 'worksheet' | 'dashboard' | 'spreadsheet' | 'cfo-home' | 'cfo-core' | 'cfo-presentation'
  active?: boolean
  tabColor?: string | null
  draggable?: boolean
  busy?: boolean
  onSelect: () => void
  onDragStart?: (event: React.DragEvent<HTMLButtonElement>) => void
  tabActions?: ExcelSheetTabActions
  spreadsheetActions?: ExcelSheetTabActions
}

export type ExcelSheetTabActions = {
  enabled: boolean
  canDuplicate?: boolean
  canMoveLeft: boolean
  canMoveRight: boolean
  canDelete: boolean
  canHide: boolean
  onRename: (name?: string) => void
  onDuplicate?: () => void
  onMoveLeft: () => void
  onMoveRight: () => void
  onMoveToIndex?: (index: number) => void
  onSetTabColor?: (color: string | null) => void
  onProtect?: () => void
  onHide: () => void
  onDelete: () => void
}

const ChevronLeftIcon = getFuseGridButtonIcon('chevron-left')!
const ChevronRightIcon = getFuseGridButtonIcon('chevron-right')!
const AddIcon = getFuseGridButtonIcon('add')!
const MoreIcon = getFuseGridButtonIcon('more-horizontal')!
const RenameIcon = getFuseGridButtonIcon('pencil')!
const DuplicateIcon = getFuseGridButtonIcon('copy-plus')!
const ColorIcon = getFuseGridButtonIcon('excel-fill-color')!
const ProtectIcon = getFuseGridButtonIcon('lock')!
const HideIcon = getFuseGridButtonIcon('eye-off')!
const UnhideIcon = getFuseGridButtonIcon('eye')!
const DeleteIcon = getFuseGridButtonIcon('trash')!

function SheetTabKindIcon({ kind, name }: { kind: ExcelSheetTabKind; name?: string }) {
  const label = String(name || '').trim().toLowerCase()
  if (kind === 'data-source') {
    return (
      <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path className="duo-fill" d="M4 6c0-1.5 3.6-2.5 8-2.5S20 4.5 20 6v12c0 1.5-3.6 2.5-8 2.5S4 19.5 4 18Z" />
        <path className="duo-stroke" d="M4 6c0 1.5 3.6 2.5 8 2.5S20 7.5 20 6 16.4 3.5 12 3.5 4 4.5 4 6Zm0 0v12c0 1.5 3.6 2.5 8 2.5s8-1 8-2.5V6M4 12c0 1.5 3.6 2.5 8 2.5s8-1 8-2.5" />
      </svg>
    )
  }
  if (kind === 'cfo-home' || kind === 'cfo-core') {
    return (
      <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path className="duo-fill" d="M12 3 13.6 9.2 20 11l-6.4 1.8L12 19l-1.6-6.2L4 11l6.4-1.8Z" />
        <path className="duo-stroke" d="M12 3 13.6 9.2 20 11l-6.4 1.8L12 19l-1.6-6.2L4 11l6.4-1.8Z" />
      </svg>
    )
  }
  if (kind === 'cfo-presentation') {
    return (
      <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path className="duo-fill" d="M5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4Z" />
        <path className="duo-stroke" d="M5.5 4h13A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18.5v-13A1.5 1.5 0 0 1 5.5 4ZM8 16.5v-3M12 16.5v-6M16 16.5v-8" />
      </svg>
    )
  }
  if (kind === 'spreadsheet' || label.includes('spreadsheet')) {
    return (
      <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect className="duo-fill" x="4" y="4" width="16" height="16" rx="2" />
        <rect className="duo-stroke" x="4" y="4" width="16" height="16" rx="2" />
        <path className="duo-stroke" d="M4 9h16M9 4v16" />
      </svg>
    )
  }
  if (label.includes('gl')) {
    return (
      <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path className="duo-fill" d="M5 5h14v14H5z" />
        <path className="duo-stroke" d="M5.5 7h13M5.5 12h13M5.5 17h8" />
      </svg>
    )
  }
  return (
    <svg className="fg-cfo-tab-ic" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path className="duo-fill" d="M12 3.4 4 9.6V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.6Z" />
      <path className="duo-stroke" d="M4 9.6 12 3.4l8 6.2V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z" />
    </svg>
  )
}

const SPREADSHEET_TAB_DRAG_MIME = 'application/x-fusegrid-spreadsheet-tab'
const SHEET_TAB_MENU_EDGE_MARGIN = 12
const SHEET_TAB_MENU_MIN_WIDTH = 220
const SHEET_TAB_MENU_MAX_HEIGHT = 480
const SHEET_TAB_COLOR_CHOICES = [
  '#107C41',
  '#2563EB',
  '#7C3AED',
  '#DC2626',
  '#D97706',
  '#0891B2',
  '#64748B',
]

export type ExcelHiddenSheetTabEntry = {
  id: string
  name: string
  enabled?: boolean
  busy?: boolean
  onUnhide: () => void
}

type ExcelSheetTabsProps = {
  tabs: ExcelSheetTabEntry[]
  hiddenSpreadsheetTabs?: ExcelHiddenSheetTabEntry[]
  showDataSourceTab?: boolean
  dataSourceActive: boolean
  linkedCfoActive?: boolean
  onSelectDataSource: () => void
  onAddSpreadsheetSheet: () => void
  canAddSpreadsheetSheet: boolean
  addSpreadsheetTitle?: string
}

type TabMenuState = {
  tabId: string
  x: number
  y: number
}

function stopMenuEvent(event: React.MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
}

function notifyForegroundQueryIntent() {
  try {
    window.dispatchEvent(new CustomEvent('fg:foreground-query-user-input'))
  } catch {
    // Best-effort only. Tab selection should still work outside the creator runtime.
  }
}

const getTabActions = (tab: ExcelSheetTabEntry): ExcelSheetTabActions | null =>
  tab.tabActions ?? tab.spreadsheetActions ?? null

export function ExcelSheetTabs({
  tabs,
  hiddenSpreadsheetTabs = [],
  showDataSourceTab = true,
  dataSourceActive,
  linkedCfoActive = false,
  onSelectDataSource,
  onAddSpreadsheetSheet,
  canAddSpreadsheetSheet,
  addSpreadsheetTitle = 'Add spreadsheet tab',
}: ExcelSheetTabsProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [menu, setMenu] = useState<TabMenuState | null>(null)
  const [draggedTabId, setDraggedTabId] = useState<string | null>(null)
  const [dragOverTabId, setDragOverTabId] = useState<string | null>(null)
  const [editingTabId, setEditingTabId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [hiddenManagerOpen, setHiddenManagerOpen] = useState(false)
  const activeMenuTab = useMemo(
    () => tabs.find((tab) => tab.id === menu?.tabId) || null,
    [menu?.tabId, tabs],
  )

  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
    }
    window.addEventListener('click', close)
    window.addEventListener('contextmenu', close)
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('contextmenu', close)
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [menu])

  const scrollByTabs = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const left = direction * Math.max(180, Math.floor(el.clientWidth * 0.72))
    if (typeof el.scrollBy === 'function') {
      el.scrollBy({ left, behavior: 'smooth' })
    } else {
      el.scrollLeft += left
    }
  }

  const openSpreadsheetMenu = (event: React.MouseEvent, tab: ExcelSheetTabEntry) => {
    stopMenuEvent(event)
    if (!getTabActions(tab)) return
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const viewportWidth = Math.max(0, window.innerWidth || 0)
    const viewportHeight = Math.max(0, window.innerHeight || 0)
    const anchorX = event.clientX || rect.left
    const anchorY = event.clientY || rect.top
    const maxLeft = Math.max(SHEET_TAB_MENU_EDGE_MARGIN, viewportWidth - SHEET_TAB_MENU_MIN_WIDTH - SHEET_TAB_MENU_EDGE_MARGIN)
    const maxTop = Math.max(SHEET_TAB_MENU_EDGE_MARGIN, viewportHeight - SHEET_TAB_MENU_MAX_HEIGHT - SHEET_TAB_MENU_EDGE_MARGIN)
    setMenu({
      tabId: tab.id,
      x: Math.max(SHEET_TAB_MENU_EDGE_MARGIN, Math.min(anchorX, maxLeft)),
      y: Math.max(SHEET_TAB_MENU_EDGE_MARGIN, Math.min(anchorY, maxTop)),
    })
  }

  const runMenuAction = (action: (() => void) | undefined, enabled: boolean) => {
    if (!enabled || !action) return
    setMenu(null)
    action()
  }

  const menuActions = activeMenuTab ? getTabActions(activeMenuTab) : null
  const visibleHiddenTabs = hiddenSpreadsheetTabs.filter((tab) => String(tab?.name ?? '').trim())
  const canUsePortal = typeof document !== 'undefined' && Boolean(document.body)

  const beginInlineRename = (tab: ExcelSheetTabEntry) => {
    if (!getTabActions(tab)?.enabled) return
    setMenu(null)
    setEditingTabId(tab.id)
    setEditingName(tab.name)
  }

  const finishInlineRename = (tab: ExcelSheetTabEntry, commit: boolean, overrideName?: string) => {
    const nextName = String(overrideName ?? editingName).trim()
    setEditingTabId(null)
    setEditingName('')
    if (!commit || !nextName || nextName === tab.name) return
    getTabActions(tab)?.onRename(nextName)
  }

  const startSpreadsheetTabDrag = (event: React.DragEvent<HTMLButtonElement>, tab: ExcelSheetTabEntry) => {
    const tabActions = getTabActions(tab)
    if (!tabActions?.enabled || !tabActions.onMoveToIndex) return
    setDraggedTabId(tab.id)
    setDragOverTabId(tab.id)
    try {
      event.dataTransfer.setData(SPREADSHEET_TAB_DRAG_MIME, tab.id)
      event.dataTransfer.effectAllowed = 'move'
    } catch {
      // Some synthetic drag events do not expose dataTransfer.
    }
  }

  const dropSpreadsheetTab = (event: React.DragEvent, targetTab: ExcelSheetTabEntry) => {
    const sourceId = draggedTabId || (() => {
      try { return event.dataTransfer.getData(SPREADSHEET_TAB_DRAG_MIME) } catch { return '' }
    })()
    setDraggedTabId(null)
    setDragOverTabId(null)
    if (!sourceId || sourceId === targetTab.id) return
    const sourceTab = tabs.find((tab) => tab.id === sourceId)
    const sourceActions = sourceTab ? getTabActions(sourceTab) : null
    if (!sourceActions?.enabled || !sourceActions.onMoveToIndex) return
    const spreadsheetTabs = tabs.filter((tab) => Boolean(getTabActions(tab)?.onMoveToIndex))
    const targetIndex = spreadsheetTabs.findIndex((tab) => tab.id === targetTab.id)
    if (targetIndex < 0) return
    event.preventDefault()
    sourceActions.onMoveToIndex(targetIndex)
  }

  return (
    <div
      className={`fg-excel-chrome fg-excel-sheet-tabs${linkedCfoActive ? ' fg-cfo-linked-tabs' : ''}`}
      data-testid="fg-sheet-tabs"
      data-cfo-linked-active={linkedCfoActive ? 'true' : undefined}
      onContextMenuCapture={(event) => {
        event.preventDefault()
      }}
    >
      <button
        type="button"
        className="fg-excel-sheet-scroll-button"
        title="Scroll sheets left"
        aria-label="Scroll sheets left"
        onClick={() => scrollByTabs(-1)}
      >
        <ChevronLeftIcon size={14} />
      </button>
      <button
        type="button"
        className="fg-excel-sheet-scroll-button"
        title="Scroll sheets right"
        aria-label="Scroll sheets right"
        onClick={() => scrollByTabs(1)}
      >
        <ChevronRightIcon size={14} />
      </button>
      <button
        type="button"
        className="fg-excel-sheet-scroll-button fg-excel-sheet-add-icon"
        title={addSpreadsheetTitle}
        aria-label="Add spreadsheet tab"
        disabled={!canAddSpreadsheetSheet}
        onClick={onAddSpreadsheetSheet}
      >
        <AddIcon size={15} />
      </button>

      <div ref={scrollRef} className="fg-excel-sheet-tab-scroll">
        {showDataSourceTab ? (
          <button
            type="button"
            className="fg-excel-sheet-tab"
            data-testid="fg-sheet-tab"
            data-sheet-kind="data-source"
            data-sheet-source-kind="data-source"
            data-sheet-name="Data Source"
            data-active={dataSourceActive ? 'true' : 'false'}
            title="Data Source sheet"
            onClick={() => {
              notifyForegroundQueryIntent()
              onSelectDataSource()
            }}
          >
            <SheetTabKindIcon kind="data-source" name="Data Source" />
            Data Source
          </button>
        ) : null}

        {tabs.map((tab) => {
          const tabActions = getTabActions(tab)
          const hasMenu = Boolean(tabActions)
          const tabTitle = `${tab.name} ${tab.kind === 'cfo-home' ? 'Home' : tab.kind === 'cfo-core' ? 'CFO Core' : tab.kind === 'cfo-presentation' ? 'Client Financials' : tab.kind === 'spreadsheet' ? 'spreadsheet' : tab.kind === 'dashboard' ? 'dashboard' : 'worksheet'} tab`
          return (
            <div
              key={tab.id}
              className="fg-excel-sheet-tab-wrap"
              data-active={tab.active ? 'true' : 'false'}
              data-busy={tab.busy ? 'true' : 'false'}
              data-dragging={draggedTabId === tab.id ? 'true' : 'false'}
              data-drag-over={dragOverTabId === tab.id && draggedTabId !== tab.id ? 'true' : 'false'}
              onContextMenu={(event) => openSpreadsheetMenu(event, tab)}
            >
              {editingTabId === tab.id ? (
                <input
                  className="fg-excel-sheet-tab fg-excel-sheet-tab-rename-input"
                  data-testid="fg-sheet-tab"
                  data-sheet-kind={tab.kind}
                  data-sheet-source-kind={tab.sourceKind ?? tab.kind}
                  data-sheet-name={tab.name}
                  data-active={tab.active ? 'true' : 'false'}
                  value={editingName}
                  autoFocus
                  title={`Rename ${tab.name}`}
                  aria-label={`Rename ${tab.name}`}
                  onChange={(event) => setEditingName(event.target.value)}
                  onInput={(event) => setEditingName(event.currentTarget.value)}
                  onBlur={() => finishInlineRename(tab, true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      finishInlineRename(tab, true, event.currentTarget.value)
                    }
                    if (event.key === 'Escape') {
                      event.preventDefault()
                      finishInlineRename(tab, false)
                    }
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="fg-excel-sheet-tab"
                  data-testid="fg-sheet-tab"
                  data-sheet-kind={tab.kind}
                  data-sheet-source-kind={tab.sourceKind ?? tab.kind}
                  data-sheet-name={tab.name}
                  data-active={tab.active ? 'true' : 'false'}
                  draggable={Boolean(tab.draggable || (tabActions?.enabled && tabActions?.onMoveToIndex))}
                  title={tabTitle}
                  onClick={() => {
                    notifyForegroundQueryIntent()
                    tab.onSelect()
                  }}
                  onDoubleClick={(event) => {
                    if (!tabActions?.enabled) return
                    event.preventDefault()
                    event.stopPropagation()
                    beginInlineRename(tab)
                  }}
                  onDragStart={(event) => {
                    if (tabActions?.onMoveToIndex) startSpreadsheetTabDrag(event, tab)
                    else tab.onDragStart?.(event)
                  }}
                  onDragEnd={() => {
                    setDraggedTabId(null)
                    setDragOverTabId(null)
                  }}
                  onDragOver={(event) => {
                    if (!draggedTabId || !tabActions?.onMoveToIndex) return
                    event.preventDefault()
                    event.dataTransfer.dropEffect = 'move'
                    setDragOverTabId(tab.id)
                  }}
                  onDrop={(event) => dropSpreadsheetTab(event, tab)}
                >
                  <SheetTabKindIcon kind={tab.kind} name={tab.name} />
                  <span className="fg-excel-sheet-tab-name">{tab.name}</span>
                  {tab.tabColor && (
                    <span
                      className="fg-excel-sheet-tab-color"
                      style={{ backgroundColor: tab.tabColor }}
                      aria-hidden="true"
                    />
                  )}
                </button>
              )}
              {hasMenu && (
                <button
                  type="button"
                  className="fg-excel-sheet-tab-more"
                  title={`${tab.name} sheet actions`}
                  aria-label={`${tab.name} sheet actions`}
                  disabled={tab.busy}
                  onClick={(event) => openSpreadsheetMenu(event, tab)}
                  onContextMenu={(event) => openSpreadsheetMenu(event, tab)}
                >
                  <MoreIcon size={14} />
                </button>
              )}
            </div>
          )
        })}
      </div>

      {menu && activeMenuTab && menuActions && canUsePortal && createPortal((
        <div
          className="fg-excel-sheet-tab-menu"
          data-testid="fg-excel-sheet-tab-menu"
          style={{ left: menu.x, top: menu.y }}
          role="menu"
          aria-label={`${activeMenuTab.name} sheet menu`}
          onClick={(event) => event.stopPropagation()}
          onContextMenu={stopMenuEvent}
        >
          <button
            type="button"
            role="menuitem"
            title={`Rename ${activeMenuTab.name}`}
            disabled={!menuActions.enabled}
            onClick={() => runMenuAction(() => beginInlineRename(activeMenuTab), menuActions.enabled)}
          >
            <RenameIcon size={14} />
            Rename
          </button>
          <button
            type="button"
            role="menuitem"
            title={`Move ${activeMenuTab.name} left`}
            disabled={!menuActions.enabled || !menuActions.canMoveLeft}
            onClick={() => runMenuAction(menuActions.onMoveLeft, menuActions.enabled && menuActions.canMoveLeft)}
          >
            <ChevronLeftIcon size={14} />
            Move Left
          </button>
          <button
            type="button"
            role="menuitem"
            title={`Move ${activeMenuTab.name} right`}
            disabled={!menuActions.enabled || !menuActions.canMoveRight}
            onClick={() => runMenuAction(menuActions.onMoveRight, menuActions.enabled && menuActions.canMoveRight)}
          >
            <ChevronRightIcon size={14} />
            Move Right
          </button>
          <div className="fg-excel-sheet-tab-menu-separator" />
          <div className="fg-excel-sheet-tab-color-menu" role="group" aria-label="Tab color">
            <div className="fg-excel-sheet-tab-color-menu-title">
              <ColorIcon size={14} />
              Tab Color
            </div>
            <div className="fg-excel-sheet-tab-color-grid">
              {SHEET_TAB_COLOR_CHOICES.map((color) => (
                <button
                  key={color}
                  type="button"
                  role="menuitem"
                  className="fg-excel-sheet-tab-color-choice"
                  title={`Set tab color ${color}`}
                  aria-label={`Set tab color ${color}`}
                  disabled={!menuActions.enabled || !menuActions.onSetTabColor}
                  data-selected={activeMenuTab.tabColor?.toUpperCase() === color ? 'true' : 'false'}
                  onClick={() => runMenuAction(() => menuActions.onSetTabColor?.(color), menuActions.enabled && Boolean(menuActions.onSetTabColor))}
                >
                  <span style={{ backgroundColor: color }} aria-hidden="true" />
                </button>
              ))}
              <button
                type="button"
                role="menuitem"
                className="fg-excel-sheet-tab-color-clear"
                title="Clear tab color"
                disabled={!menuActions.enabled || !menuActions.onSetTabColor}
                onClick={() => runMenuAction(() => menuActions.onSetTabColor?.(null), menuActions.enabled && Boolean(menuActions.onSetTabColor))}
              >
                None
              </button>
            </div>
          </div>
          <div className="fg-excel-sheet-tab-menu-separator" />
          <button
            type="button"
            role="menuitem"
            title={`Protect ${activeMenuTab.name}`}
            disabled={!menuActions.enabled || !menuActions.onProtect}
            onClick={() => runMenuAction(menuActions.onProtect, menuActions.enabled && Boolean(menuActions.onProtect))}
          >
            <ProtectIcon size={14} />
            Protect Sheet
          </button>
          <button
            type="button"
            role="menuitem"
            title={menuActions.canDuplicate === false ? `${activeMenuTab.name} cannot be duplicated right now` : `Duplicate ${activeMenuTab.name}`}
            disabled={!menuActions.enabled || menuActions.canDuplicate === false || !menuActions.onDuplicate}
            onClick={() => runMenuAction(menuActions.onDuplicate, menuActions.enabled && menuActions.canDuplicate !== false && Boolean(menuActions.onDuplicate))}
          >
            <DuplicateIcon size={14} />
            Duplicate
          </button>
          <button
            type="button"
            role="menuitem"
            title="Copying sheets to another workbook needs a workbook transfer API"
            disabled
          >
            <DuplicateIcon size={14} />
            Copy to Workbook
          </button>
          <button
            type="button"
            role="menuitem"
            title="Moving sheets to another workbook needs a workbook transfer API"
            disabled
          >
            <ChevronRightIcon size={14} />
            Move to Workbook
          </button>
          <button
            type="button"
            role="menuitem"
            title={menuActions.canHide ? `Hide ${activeMenuTab.name}` : `${activeMenuTab.name} cannot be hidden because at least one sheet must remain visible`}
            disabled={!menuActions.enabled || !menuActions.canHide}
            onClick={() => runMenuAction(menuActions.onHide, menuActions.enabled && menuActions.canHide)}
          >
            <HideIcon size={14} />
            Hide
          </button>
          {visibleHiddenTabs.length > 0 ? (
            <button
              type="button"
              role="menuitem"
              title="Manage hidden sheets"
              onClick={() => {
                setMenu(null)
                setHiddenManagerOpen(true)
              }}
            >
              <UnhideIcon size={14} />
              Manage Hidden Sheets
            </button>
          ) : null}
          {visibleHiddenTabs.length > 0 ? (
            visibleHiddenTabs.map((hiddenTab) => (
              <button
                key={hiddenTab.id}
                type="button"
                role="menuitem"
                title={`Unhide ${hiddenTab.name}`}
                disabled={hiddenTab.enabled === false || hiddenTab.busy}
                onClick={() => runMenuAction(hiddenTab.onUnhide, hiddenTab.enabled !== false && !hiddenTab.busy)}
              >
                <UnhideIcon size={14} />
                Unhide {hiddenTab.name}
              </button>
            ))
          ) : (
            <button type="button" role="menuitem" title="No hidden sheets to unhide" disabled>
              <UnhideIcon size={14} />
              Unhide
            </button>
          )}
          <div className="fg-excel-sheet-tab-menu-separator" />
          <button
            type="button"
            role="menuitem"
            className="fg-excel-sheet-tab-menu-danger"
            title={`Delete ${activeMenuTab.name}`}
            disabled={!menuActions.enabled || !menuActions.canDelete}
            onClick={() => runMenuAction(menuActions.onDelete, menuActions.enabled && menuActions.canDelete)}
          >
            <DeleteIcon size={14} />
            Delete
          </button>
        </div>
      ), document.body)}
      {hiddenManagerOpen && canUsePortal && createPortal((
        <div className="fg-excel-sheet-hidden-backdrop" role="presentation" onMouseDown={() => setHiddenManagerOpen(false)}>
          <section
            className="fg-excel-sheet-hidden-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Hidden sheets"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header>
              <strong>Hidden Sheets</strong>
              <button type="button" title="Close hidden sheets" aria-label="Close hidden sheets" onClick={() => setHiddenManagerOpen(false)}>Close</button>
            </header>
            <div className="fg-excel-sheet-hidden-list">
              {visibleHiddenTabs.length > 0 ? visibleHiddenTabs.map((hiddenTab) => (
                <div key={hiddenTab.id} className="fg-excel-sheet-hidden-row">
                  <span title={hiddenTab.name}>{hiddenTab.name}</span>
                  <button
                    type="button"
                    title={`Unhide ${hiddenTab.name}`}
                    disabled={hiddenTab.enabled === false || hiddenTab.busy}
                    onClick={() => {
                      hiddenTab.onUnhide()
                      setHiddenManagerOpen(false)
                    }}
                  >
                    Unhide
                  </button>
                </div>
              )) : (
                <div className="fg-excel-sheet-hidden-empty">No hidden sheets</div>
              )}
            </div>
          </section>
        </div>
      ), document.body)}
    </div>
  )
}
